import { Autocomplete, Box, Skeleton, Stack, TextField } from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar, useTrainedModels } from "../../../hooks";

import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import PropTypes from "prop-types";

/**
 * Two cascading selectors (TrainedModel → TrainedModelVersion) for jobs
 * whose spec includes `trainedModelFilters`. Picking a version stores the
 * full `TrainedModelVersion` object at `preExec.trainedModelVersion`, so
 * the submit encoder can read `weightsLocation` / `configLocation`
 * directly from the form value without any lookup.
 *
 * On first successful fetch, runs a one-shot reverse lookup against
 * `preExecArgs[0]`'s `downloadModelConfigFile.from` and
 * `downloadModelWeightsFile.from` to resolve the initial selection.
 *
 * This outer component is a thin shell that handles the SWR fetch state
 * (skeleton while loading, FetchErrorAlert on error). The inner
 * `TrainedModelSelectorReady` runs the form logic with `trainedModels`
 * guaranteed to be available.
 */
TrainedModelSelector.propTypes = {
    jobName: PropTypes.string.isRequired,
    trainedModelFilters: PropTypes.shape({
        kind: PropTypes.string,
        task: PropTypes.string,
        system: PropTypes.string,
    }).isRequired,
    preExecArgs: PropTypes.arrayOf(PropTypes.string).isRequired,
    experimentCropUuid: PropTypes.string,
    disabled: PropTypes.bool,
};

export function TrainedModelSelector({
    jobName,
    trainedModelFilters,
    preExecArgs,
    experimentCropUuid,
    disabled,
}) {
    // Validate preExec.args[0] shape; fail loudly if malformed (runs
    // before the SWR call so bad data fails immediately on mount).
    const parsedPreExec = useMemo(
        () => parsePreExecArgs(preExecArgs, jobName),
        [preExecArgs, jobName]
    );

    const { data, error, isLoading } = useTrainedModels(trainedModelFilters);

    if (isLoading) {
        return (
            <FieldsetShell>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
            </FieldsetShell>
        );
    }
    if (error) return <FetchErrorAlert error={error} />;

    return (
        <TrainedModelSelectorReady
            jobName={jobName}
            parsedPreExec={parsedPreExec}
            trainedModels={data.rows}
            experimentCropUuid={experimentCropUuid}
            disabled={disabled}
        />
    );
}

TrainedModelSelectorReady.propTypes = {
    jobName: PropTypes.string.isRequired,
    parsedPreExec: PropTypes.shape({
        configFrom: PropTypes.string,
        weightsFrom: PropTypes.string,
    }).isRequired,
    trainedModels: PropTypes.array.isRequired,
    experimentCropUuid: PropTypes.string,
    disabled: PropTypes.bool,
};

function TrainedModelSelectorReady({
    jobName,
    parsedPreExec,
    trainedModels,
    experimentCropUuid,
    disabled,
}) {
    const { control, setValue } = useFormContext();
    const { openSnackbar } = useSnackbar();

    const trainedModelVersion = useWatch({
        control,
        name: `${jobName}.preExec.trainedModelVersion`,
    });

    // Resolve the parent TrainedModel of the currently selected version
    // by looking it up in the fetched list via trainedModelUuid.
    const formTrainedModel = useMemo(() => {
        if (!trainedModelVersion) return null;
        return (
            trainedModels.find(
                (tm) => tm.uuid === trainedModelVersion.trainedModelUuid
            ) ?? null
        );
    }, [trainedModels, trainedModelVersion]);

    // Trained models sorted so that, when rendered with `groupBy`, they are
    // grouped by crop: the experiment's own crop first, the remaining crop
    // groups alphabetically, and models sorted alphabetically within each
    // group. See `sortTrainedModelsByCropGroup` for the full ordering.
    const sortedTrainedModels = useMemo(
        () => sortTrainedModelsByCropGroup(trainedModels, experimentCropUuid),
        [trainedModels, experimentCropUuid]
    );

    // Local UI state for the model selector. Synced with the form-derived
    // model so external updates (reverse lookup, future preset support)
    // also update the visible model.
    const [selectedTrainedModel, setSelectedTrainedModel] = useState(null);
    useEffect(() => {
        if (formTrainedModel) setSelectedTrainedModel(formTrainedModel);
    }, [formTrainedModel]);

    // Active versions of the selected model, latest first.
    const activeVersionsDescending = useMemo(
        () =>
            sortVersionsDescending(
                (selectedTrainedModel?.TrainedModelVersions ?? []).filter(
                    (version) => version.active
                )
            ),
        [selectedTrainedModel]
    );

    // One-shot initial selection on mount. First tries to restore a prior
    // selection from `preExec.args` (reverse lookup); when there is nothing
    // to restore, falls back to auto-selecting a model for the experiment's
    // crop. Either way this only runs on initial load, so the user is free
    // to clear the field afterwards.
    const reverseLookupDoneRef = useRef(false);
    useEffect(() => {
        if (reverseLookupDoneRef.current) return;
        reverseLookupDoneRef.current = true;

        if (trainedModelVersion != null) return;

        const result = findInitialSelection(
            trainedModels,
            parsedPreExec.configFrom,
            parsedPreExec.weightsFrom
        );

        if (result.kind === "match") {
            setValue(`${jobName}.preExec.trainedModelVersion`, result.version, {
                shouldDirty: false,
            });
            return;
        }

        if (result.kind === "warning") {
            openSnackbar(`${jobName}: ${result.message}`, "warning");
        } else if (result.kind === "hint") {
            openSnackbar(
                `${jobName}: previously selected trained model is no longer available.`,
                "info"
            );
        }

        // Fallback: auto-select the best model for the experiment's crop.
        const autoModel = pickAutoSelectedModel(
            trainedModels,
            experimentCropUuid
        );
        const autoVersion = autoModel && pickDefaultVersion(autoModel);
        if (autoVersion) {
            setValue(`${jobName}.preExec.trainedModelVersion`, autoVersion, {
                shouldDirty: false,
            });
        }
    }, [
        trainedModels,
        parsedPreExec,
        trainedModelVersion,
        experimentCropUuid,
        jobName,
        setValue,
        openSnackbar,
    ]);

    return (
        <FieldsetShell>
            <Autocomplete
                options={sortedTrainedModels}
                groupBy={(tm) => getCropGroupLabel(tm)}
                getOptionLabel={(tm) => tm.name}
                isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                value={selectedTrainedModel}
                disabled={disabled}
                onChange={(_, newValue) => {
                    setSelectedTrainedModel(newValue);
                    setValue(`${jobName}.preExec.trainedModelVersion`, null);
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Model" />
                )}
                fullWidth
            />
            <Controller
                control={control}
                name={`${jobName}.preExec.trainedModelVersion`}
                disabled={disabled}
                rules={{
                    required: `Trained model version required for "${jobName}"`,
                }}
                render={({ field, fieldState }) => (
                    <Autocomplete
                        options={activeVersionsDescending}
                        getOptionLabel={(v) => v.version}
                        isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                        value={field.value ?? null}
                        onChange={(_, newValue) =>
                            field.onChange(newValue ?? null)
                        }
                        onBlur={field.onBlur}
                        disabled={field.disabled || !selectedTrainedModel}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                name={field.name}
                                inputRef={field.ref}
                                label="Version"
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message}
                            />
                        )}
                        fullWidth
                    />
                )}
            />
        </FieldsetShell>
    );
}

FieldsetShell.propTypes = {
    children: PropTypes.node.isRequired,
};

function FieldsetShell({ children }) {
    return (
        <Box
            component="fieldset"
            className="nodrag nopan nowheel"
            sx={{
                border: "2px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                mx: 1,
                my: 1,
                p: 1,
            }}
        >
            <Box
                component="legend"
                sx={{
                    px: 0.5,
                    fontSize: "1rem",
                    color: "text.secondary",
                }}
            >
                Trained Model
            </Box>
            <Stack direction="column" spacing={1}>
                {children}
            </Stack>
        </Box>
    );
}

// Label used for models that have no associated crop; sorted to the end.
const OTHER_CROP_GROUP_LABEL = "Other";

/**
 * Build the crop-group label for a trained model. The names of the crops it
 * is associated with (via `TrainedModelCrops`, each carrying a nested
 * `Crop`) are sorted alphabetically and joined, so a model tied to several
 * crops forms its own combined group (e.g. "Barley, Wheat"). Models with no
 * associated crop fall into the trailing "Other" group.
 */
function getCropGroupLabel(trainedModel) {
    const names = (trainedModel.TrainedModelCrops ?? [])
        .map((tmc) => tmc.Crop?.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    return names.length > 0 ? names.join(", ") : OTHER_CROP_GROUP_LABEL;
}

/**
 * Ordering rank of a model's crop group:
 *   0 - the model is tied to exactly the experiment's single crop,
 *   1 - a multi-crop group that includes the experiment's crop,
 *   2 - any other crop (or crop combination),
 *   3 - no associated crop ("Other").
 */
function getCropGroupRank(trainedModel, experimentCropUuid) {
    const crops = trainedModel.TrainedModelCrops ?? [];
    if (crops.length === 0) return 3;

    const includesExperimentCrop =
        experimentCropUuid != null &&
        crops.some((tmc) => tmc.cropUuid === experimentCropUuid);
    if (!includesExperimentCrop) return 2;

    return crops.length === 1 ? 0 : 1;
}

/**
 * Sort trained models so that rendering with `groupBy={getCropGroupLabel}`
 * yields crop groups in the desired order: the experiment's own crop first,
 * then multi-crop groups that include the experiment's crop (alphabetically),
 * then the remaining crop groups (alphabetically), then the "Other" group;
 * models are sorted alphabetically by name within each group. Options must
 * be pre-sorted this way because MUI's `groupBy` only groups consecutive
 * options and does not reorder them.
 */
function sortTrainedModelsByCropGroup(trainedModels, experimentCropUuid) {
    return [...trainedModels].sort((a, b) => {
        const rankA = getCropGroupRank(a, experimentCropUuid);
        const rankB = getCropGroupRank(b, experimentCropUuid);
        if (rankA !== rankB) return rankA - rankB;

        const labelA = getCropGroupLabel(a);
        const labelB = getCropGroupLabel(b);
        if (labelA !== labelB) return labelA.localeCompare(labelB);

        return a.name.localeCompare(b.name);
    });
}

/**
 * Sort trained-model versions alphanumerically by their `version` field in
 * descending order (natural sort, so "003" > "002" > "001"), i.e. latest
 * first. Does not mutate the input.
 */
function sortVersionsDescending(versions) {
    return [...versions].sort((a, b) =>
        b.version.localeCompare(a.version, undefined, { numeric: true })
    );
}

/**
 * The version to auto-select for a model: the one flagged `default`, or
 * (failing that) the latest active version. Only active versions are
 * considered, matching what the Version selector offers. Returns `null`
 * when the model has no active version.
 */
function pickDefaultVersion(trainedModel) {
    const activeVersions = sortVersionsDescending(
        (trainedModel.TrainedModelVersions ?? []).filter((v) => v.active)
    );
    if (activeVersions.length === 0) return null;
    return activeVersions.find((v) => v.default) ?? activeVersions[0];
}

/**
 * Pick the trained model to auto-select for the experiment's crop, among
 * those whose crop set includes it. The most generic model wins (the one
 * covering the most crops); ties are broken alphabetically by name.
 * Returns `null` when the crop is unknown or no model covers it.
 */
function pickAutoSelectedModel(trainedModels, experimentCropUuid) {
    if (experimentCropUuid == null) return null;

    const candidates = trainedModels.filter((tm) =>
        (tm.TrainedModelCrops ?? []).some(
            (tmc) => tmc.cropUuid === experimentCropUuid
        )
    );
    if (candidates.length === 0) return null;

    return [...candidates].sort((a, b) => {
        const countA = a.TrainedModelCrops?.length ?? 0;
        const countB = b.TrainedModelCrops?.length ?? 0;
        if (countA !== countB) return countB - countA;
        return a.name.localeCompare(b.name);
    })[0];
}

function parsePreExecArgs(preExecArgs, jobName) {
    if (!preExecArgs || preExecArgs.length === 0) {
        throw new Error(`Job ${jobName}: preExec.args is missing or empty`);
    }
    const blob = JSON.parse(preExecArgs[0]);
    if (!blob.downloadModelConfigFile || !blob.downloadModelWeightsFile) {
        throw new Error(
            `Job ${jobName}: preExec args[0] is missing downloadModelConfigFile or downloadModelWeightsFile`
        );
    }
    return {
        configFrom: blob.downloadModelConfigFile.from,
        weightsFrom: blob.downloadModelWeightsFile.from,
    };
}

function findInitialSelection(trainedModels, configFrom, weightsFrom) {
    if (configFrom == null && weightsFrom == null) {
        return { kind: "none" };
    }
    if (configFrom == null || weightsFrom == null) {
        return {
            kind: "warning",
            message:
                "trained model state is incomplete (only one of config/weights is set). No initial selection.",
        };
    }
    for (const tm of trainedModels) {
        const v = tm.TrainedModelVersions?.find(
            (v) =>
                v.configLocation === configFrom &&
                v.weightsLocation === weightsFrom
        );
        if (v) return { kind: "match", trainedModel: tm, version: v };
    }
    const anyPartial = trainedModels.some((tm) =>
        tm.TrainedModelVersions?.some(
            (v) =>
                v.configLocation === configFrom ||
                v.weightsLocation === weightsFrom
        )
    );
    if (anyPartial) {
        return {
            kind: "warning",
            message:
                "trained model state is inconsistent (config and weights point to different versions). No initial selection.",
        };
    }
    return { kind: "hint" };
}
